import React, { useState, useCallback, useContext, useMemo, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { DataTable, Button, SparkIcon, TooltipTrigger, PopoverMenu, PopoverMenuItem, PopoverShell, Modal, SparkModalPortal, TagFeedback, ProgressBar, Spinner } from '@spark';
import { isGenerativeTaskLoose } from '@/shared/domain/taskTypes';
import { formatDate, formatTime } from '@/shared/utils/helpers';
import {
    formatTrainingModelDetailsTitle,
    getUsecaseCount,
    shouldShowTrainingSubObjective,
} from './trainingsUtils';
import { GlobalContext } from '@/app/providers/globalState';
import { deploy_in_progress, undeploy_in_progress } from '@/shared/constants/taskStatuses';
import ModelDetailsDrawer from './ModelDetailsDrawer';
import QueryFileModal from '@/features/tasks/components/Task/QueryFileModal';
import { useAuth } from '@/features/auth/AuthContext';
import { getEffectiveProgress } from '@/features/trainings/utils/llmJudgeProgress';
import { getStatusLabel } from '@/features/trainings/utils/trainingUtils';
import { QUERY_FILE_LANGUAGES } from '@/features/trainings/utils/queryFileGenerator';
import './TrainingsGroupedView.css';

// ─── Local Spark-based helper components ────────────────────────────────────

const EllipsisText = ({ text, maxWidth = 180, style = {}, className = '', onClick, ...props }) => {
    const [isTruncated, setIsTruncated] = useState(false);
    const ref = useRef(null);

    const updateTruncation = useCallback(() => {
        const el = ref.current;
        if (!el) return;
        if (el.clientWidth === 0 && el.scrollWidth === 0) return;
        setIsTruncated(el.scrollWidth > el.clientWidth + 1);
    }, []);

    useLayoutEffect(() => {
        updateTruncation();
        const el = ref.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(() => requestAnimationFrame(updateTruncation));
        ro.observe(el);
        return () => ro.disconnect();
    }, [text, maxWidth, updateTruncation]);

    const content = (
        <div
            ref={ref}
            className={className}
            style={{
                maxWidth,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
                ...style,
            }}
            onClick={onClick}
            {...props}
        >
            {text}
        </div>
    );

    return isTruncated ? (
        <TooltipTrigger text={text}>
            {content}
        </TooltipTrigger>
    ) : content;
};

const ClickableNameLocal = ({ text, onClick, maxWidth = 320, style = {} }) => (
    <EllipsisText
        text={text}
        maxWidth={maxWidth}
        className="fs-unmask"
        style={{
            fontSize: 15,
            color: 'var(--semantic-content-primary)',
            cursor: 'pointer',
            textDecoration: 'underline',
            textDecorationColor: 'var(--semantic-content-link)',
            textDecorationThickness: '1px',
            textUnderlineOffset: '2px',
            ...style,
        }}
        onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClick?.();
        }}
    />
);

const ClickableVersionLocal = ({ version, onClick, tooltipTitle }) => {
    const versionText = version?.version ? `V${version.version}.0` : 'V1.0';
    const handleClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick?.(version);
    };

    const content = (
        <span
            className="fs-unmask"
            style={{
                fontSize: 15,
                color: 'var(--semantic-content-primary)',
                cursor: 'pointer',
                textDecoration: 'underline',
                textDecorationColor: 'var(--semantic-content-link)',
                textDecorationThickness: '1px',
                textUnderlineOffset: '2px',
            }}
            onClick={handleClick}
        >
            {versionText}
        </span>
    );

    return tooltipTitle ? (
        <TooltipTrigger text={tooltipTitle}>
            {content}
        </TooltipTrigger>
    ) : content;
};

const DateTimeDisplay = ({ value, emptyPlaceholder = '' }) => {
    const dateStr = formatDate(value);
    const timeStr = formatTime(value);
    if (!dateStr) return <span>{emptyPlaceholder}</span>;
    if (!timeStr) return <span className="fs-unmask" style={{ fontSize: 14 }}>{dateStr}</span>;
    return (
        <TooltipTrigger text={timeStr}>
            <span className="fs-unmask" style={{ fontSize: 14 }}>{dateStr}</span>
        </TooltipTrigger>
    );
};

// ─── Status helpers ─────────────────────────────────────────────────────────

const getStatusVariant = (status) => {
    const map = {
        'running': 'processing',
        'completed': 'success',
        'failed': 'error',
        'waiting': 'default',
        'preparing': 'processing',
        'paused': 'warning',
        'ready to train': 'default',
        'training': 'processing',
        'processing': 'processing',
        'in progress': 'processing',
    };
    return map[status?.toLowerCase()] || 'default';
};

const DurationDisplay = ({ createdDatetime, finishedDatetime, status }) => {
    const [currentTime, setCurrentTime] = useState(new Date());
    const isRunning = status?.toLowerCase() === 'running' ||
        (status?.toLowerCase() !== 'completed' &&
            status?.toLowerCase() !== 'failed' &&
            !finishedDatetime);

    useEffect(() => {
        if (isRunning) {
            const interval = setInterval(() => setCurrentTime(new Date()), 1000);
            return () => clearInterval(interval);
        }
    }, [isRunning]);

    const calculateDuration = () => {
        if (!createdDatetime) return '-';
        if (status?.toLowerCase() === 'failed' && !finishedDatetime) return '-';

        const startTimeUTC = createdDatetime.includes('Z') ? createdDatetime : createdDatetime + 'Z';
        const start = new Date(startTimeUTC);

        const endTimeUTC = finishedDatetime && !finishedDatetime.includes('Z')
            ? finishedDatetime + 'Z'
            : finishedDatetime;
        const end = endTimeUTC ? new Date(endTimeUTC) : currentTime;

        const diffMs = end - start;
        if (diffMs < 0) return '-';

        const seconds = Math.floor(diffMs / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        if (days > 0) return `${days}d ${hours % 24}h`;
        if (hours > 0) return `${hours}h ${minutes % 60}m`;
        if (minutes > 0) return `${minutes}m`;
        return `${seconds}s`;
    };

    return (
        <span className="fs-unmask" style={{ fontSize: 14, color: 'var(--semantic-content-primary)' }}>
            {calculateDuration()}
            {isRunning && (
                <span className="fs-unmask" style={{ color: 'var(--semantic-content-secondary)', fontSize: 12, marginLeft: 4 }}>
                    (running)
                </span>
            )}
        </span>
    );
};

const StatusDisplay = ({ status, progress, trainingId, errorDetails: initialErrorDetails, isDeployed, usecaseStatus, version, deployingVersion, undeployingVersion, usecaseId }) => {
    const { trainStatuses, evaluationProgress } = useContext(GlobalContext);

    const trainingStatus = trainStatuses[trainingId] || {
        progress: progress,
        status: status,
        error_details: null,
    };

    const errorDetails = trainingStatus.error_details || initialErrorDetails;
    const isFailed = trainingStatus.status?.toLowerCase() === 'failed';

    const effectiveProgress = getEffectiveProgress(
        trainingStatus.status,
        trainingStatus.progress,
        evaluationProgress,
        usecaseId || trainingStatus.usecase_id,
        version,
    );
    const isCompleted = trainingStatus.status?.toLowerCase() === 'completed';
    const hasProgress = effectiveProgress !== undefined && effectiveProgress !== null && effectiveProgress >= 0;

    const getProgressType = () => {
        if (trainingStatus.status === 'failed') return 'error';
        if (trainingStatus.status === 'paused') return 'disabled';
        return 'default';
    };

    if (!isCompleted && hasProgress) {
        const progressBar = (
            <div className="fs-unmask" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 60 }}>
                    <ProgressBar
                        value={effectiveProgress}
                        type={getProgressType()}
                        showPercentage={false}
                    />
                </div>
                <span style={{ fontSize: 12, color: 'var(--semantic-content-secondary)', minWidth: 30, textAlign: 'right' }}>
                    {effectiveProgress}%
                </span>
                <TagFeedback
                    label={getStatusLabel(trainingStatus.status)}
                    variant={getStatusVariant(trainingStatus.status)}
                />
            </div>
        );

        if (isFailed && errorDetails) {
            return (
                <TooltipTrigger text={`Training Failed: ${errorDetails}`} placement="top">
                    <div className="fs-unmask" style={{ cursor: 'pointer' }}>
                        {progressBar}
                    </div>
                </TooltipTrigger>
            );
        }

        return progressBar;
    }

    const statusText = trainingStatus.status === 'running' ? 'Training...' : trainingStatus.status;
    const isDeployInProgress = usecaseStatus === deploy_in_progress &&
        deployingVersion !== undefined && deployingVersion === version;
    const isUndeployInProgress = usecaseStatus === undeploy_in_progress &&
        undeployingVersion !== undefined && undeployingVersion === version;
    const showLoading = isDeployInProgress || isUndeployInProgress;
    const deployedText = isDeployed ? ' (deployed)' : '';

    const statusTag = (
        <TagFeedback
            label={statusText}
            variant={getStatusVariant(trainingStatus.status)}
        />
    );

    return (
        <div className="fs-unmask" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {isFailed && errorDetails ? (
                <TooltipTrigger text={`Training Failed: ${errorDetails}`} placement="top">
                    <span className="fs-unmask" style={{ cursor: 'pointer' }}>
                        {statusTag}
                    </span>
                </TooltipTrigger>
            ) : (
                statusTag
            )}
            {showLoading ? (
                <Spinner size="small" />
            ) : (
                <span className="fs-unmask" style={{ color: 'var(--semantic-content-secondary)', fontSize: 12, marginLeft: 4 }}>
                    {deployedText}
                </span>
            )}
        </div>
    );
};

// ─── Actions dropdown using PopoverMenu ─────────────────────────────────────

const SUBMENU_CLOSE_DELAY_MS = 150;
const SUBMENU_GAP = 4;

/**
 * Popover row that opens a flyout submenu to the right (Spark cascading
 * chevron). Used for "Query file" → Python / TypeScript / Go.
 */
const CascadingPopoverItem = ({ item }) => {
    const [open, setOpen] = useState(false);
    const itemRef = useRef(null);
    const closeTimer = useRef(null);
    const [style, setStyle] = useState({
        position: 'fixed',
        visibility: 'hidden',
        zIndex: 10001,
    });

    const cancelClose = () => {
        if (closeTimer.current) {
            clearTimeout(closeTimer.current);
            closeTimer.current = null;
        }
    };

    const scheduleClose = () => {
        cancelClose();
        closeTimer.current = setTimeout(() => setOpen(false), SUBMENU_CLOSE_DELAY_MS);
    };

    useLayoutEffect(() => {
        if (!open) return undefined;
        const update = () => {
            const el = itemRef.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            const spaceRight = window.innerWidth - rect.right;
            const openLeft = spaceRight < 160 && rect.left > spaceRight;
            setStyle({
                position: 'fixed',
                top: rect.top,
                zIndex: 10001,
                ...(openLeft
                    ? { right: window.innerWidth - rect.left + SUBMENU_GAP }
                    : { left: rect.right + SUBMENU_GAP }),
            });
        };
        update();
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);
        return () => {
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
        };
    }, [open]);

    useEffect(() => () => cancelClose(), []);

    return (
        <>
            <div
                ref={itemRef}
                onMouseEnter={() => {
                    cancelClose();
                    setOpen(true);
                }}
                onMouseLeave={scheduleClose}
            >
                <PopoverMenuItem
                    type="cascading"
                    label={item.label}
                    visualState={open ? 'hover' : undefined}
                    onClick={() => setOpen((value) => !value)}
                />
            </div>
            {open &&
                createPortal(
                    <div
                        className="trainings-actions-submenu"
                        style={style}
                        onMouseEnter={cancelClose}
                        onMouseLeave={scheduleClose}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <PopoverMenu tip={false} items={item.children} />
                    </div>,
                    document.body
                )}
        </>
    );
};

const VersionActionsMenu = ({
    version,
    role,
    isThisVersionDeployed,
    hasAnyDeployedVersion,
    openModelDetails,
    handleDownload,
    handlePushModel,
    onDeployTraining,
    onUndeployTraining,
    onDeleteTraining,
    taskType,
    onOpenQueryFile,
}) => {
    const [open, setOpen] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteIsStop, setDeleteIsStop] = useState(false);
    const [menuStyle, setMenuStyle] = useState({ position: 'fixed', visibility: 'hidden', zIndex: 10000 });
    const wrapRef = useRef(null);
    const panelRef = useRef(null);

    const status = version?.status?.toLowerCase();
    const isGenerative = isGenerativeTaskLoose(taskType);

    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (wrapRef.current?.contains(e.target)) return;
            if (panelRef.current?.contains(e.target)) return;
            if (e.target.closest?.('.trainings-actions-submenu')) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    // Position the portaled menu next to the trigger; flip upward when there
    // isn't enough viewport space below (avoids scrolling for bottom rows).
    useLayoutEffect(() => {
        if (!open) return undefined;
        const update = () => {
            const trigger = wrapRef.current;
            if (!trigger) return;
            const rect = trigger.getBoundingClientRect();
            const menuHeight = panelRef.current?.offsetHeight || 0;
            const spaceBelow = window.innerHeight - rect.bottom;
            const openUp = menuHeight > 0 && spaceBelow < menuHeight + 8 && rect.top > spaceBelow;
            setMenuStyle({
                position: 'fixed',
                right: window.innerWidth - rect.right,
                zIndex: 10000,
                ...(openUp
                    ? { bottom: window.innerHeight - rect.top + 4 }
                    : { top: rect.bottom + 4 }),
            });
        };
        update();
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);
        return () => {
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
        };
    }, [open]);

    // Position the portaled menu next to the trigger; flip upward when there
    // isn't enough viewport space below (avoids scrolling for bottom rows).
    useLayoutEffect(() => {
        if (!open) return undefined;
        const update = () => {
            const trigger = wrapRef.current;
            if (!trigger) return;
            const rect = trigger.getBoundingClientRect();
            const menuHeight = panelRef.current?.offsetHeight || 0;
            const spaceBelow = window.innerHeight - rect.bottom;
            const openUp = menuHeight > 0 && spaceBelow < menuHeight + 8 && rect.top > spaceBelow;
            setMenuStyle({
                position: 'fixed',
                right: window.innerWidth - rect.right,
                zIndex: 10000,
                ...(openUp
                    ? { bottom: window.innerHeight - rect.top + 4 }
                    : { top: rect.bottom + 4 }),
            });
        };
        update();
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);
        return () => {
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
        };
    }, [open]);

    const items = useMemo(() => {
        const menuItems = [
            {
                label: 'Model details',
                onClick: () => {
                    setOpen(false);
                    openModelDetails({
                        ...version,
                        isThisVersionDeployed,
                        hasAnyDeployedVersion,
                    });
                },
            },
        ];

        if (isThisVersionDeployed || status === 'completed') {
            menuItems.push({
                label: 'Query file',
                type: 'cascading',
                children: QUERY_FILE_LANGUAGES.map(({ key, label }) => ({
                    label,
                    suffixIconSlug: 'eye',
                    onClick: () => {
                        setOpen(false);
                        onOpenQueryFile(version, key);
                    },
                })),
            });
        }

        if (status === 'completed') {
            menuItems.push({
                label: 'Download',
                state: role === 'Viewer' ? 'disabled' : 'default',
                onClick: role === 'Viewer' ? undefined : () => {
                    setOpen(false);
                    handleDownload(version);
                },
            });
            menuItems.push({
                label: 'Push model',
                state: role === 'Viewer' ? 'disabled' : 'default',
                disableWhenReadOnly: true,
                onClick: role === 'Viewer' ? undefined : () => {
                    setOpen(false);
                    handlePushModel(version);
                },
            });
        }

        if (role !== 'Viewer' && status === 'completed' && isThisVersionDeployed && !isGenerative) {
            menuItems.push({
                label: 'Undeploy',
                disableWhenReadOnly: true,
                onClick: () => {
                    setOpen(false);
                    onUndeployTraining && onUndeployTraining(version);
                },
            });
        }

        if (role !== 'Viewer' && status === 'completed' && !hasAnyDeployedVersion && !isGenerative) {
            menuItems.push({
                label: 'Deploy',
                disableWhenReadOnly: true,
                onClick: () => {
                    setOpen(false);
                    onDeployTraining && onDeployTraining(version);
                },
            });
        }

        if (role !== 'Viewer') {
            const isRunning =
                status === 'waiting' ||
                status === 'preparing' ||
                status === 'running' ||
                status === 'training' ||
                status === 'processing';

            menuItems.push({
                label: isRunning ? 'Stop' : 'Delete',
                disableWhenReadOnly: true,
                onClick: () => {
                    setOpen(false);
                    setDeleteIsStop(isRunning);
                    setShowDeleteConfirm(true);
                },
            });
        }

        return menuItems;
    }, [version, role, isThisVersionDeployed, hasAnyDeployedVersion, status, isGenerative, openModelDetails, handleDownload, handlePushModel, onDeployTraining, onUndeployTraining, onOpenQueryFile]);

    return (
        <>
            <div ref={wrapRef} style={{ position: 'relative', display: 'inline-flex' }}>
                <Button
                    variant="ghost"
                    size="small"
                    aria-label="Model actions"
                    icon={<SparkIcon slug="dots" size="small" />}
                    onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
                />
            </div>
            {open && createPortal(
                <div ref={panelRef} style={menuStyle} onClick={(e) => e.stopPropagation()}>
                    <PopoverShell
                        tip={false}
                        panelClassName="spark-popover-menu-panel spark-popover-menu-panel-regular"
                    >
                        <div className="spark-popover-menu-list" role="menu">
                            {items.map((item, index) =>
                                item.children?.length ? (
                                    <CascadingPopoverItem
                                        key={`${item.label}-${index}`}
                                        item={item}
                                    />
                                ) : (
                                    <PopoverMenuItem
                                        key={`${item.label}-${index}`}
                                        {...item}
                                    />
                                )
                            )}
                        </div>
                    </PopoverShell>
                </div>,
                document.body,
            )}

            <SparkModalPortal
                open={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                size="text"
            >
                <Modal
                    className="trainings-confirm-modal"
                    feedbackType="warning"
                    title={deleteIsStop ? 'Stop training?' : 'Delete model?'}
                    showSubtitle={false}
                    showScrollBar={false}
                    fixedHeight={false}
                    danger
                    primaryLabel={deleteIsStop ? 'Stop' : 'Delete'}
                    primaryDisableWhenReadOnly
                    tertiaryLabel="Cancel"
                    onPrimaryClick={() => {
                        setShowDeleteConfirm(false);
                        onDeleteTraining(version);
                    }}
                    onTertiaryClick={() => setShowDeleteConfirm(false)}
                    onCloseClick={() => setShowDeleteConfirm(false)}
                >
                    <p>
                        {deleteIsStop
                            ? 'Are you sure you want to stop this training? This action is permanent and will remove all the data associated to this model.'
                            : 'Are you sure you want to delete this model? This action is permanent and will remove all the data associated to this model.'}
                    </p>
                    <p style={{ color: 'var(--semantic-feedback-critical-on-surface)' }}>
                        This action cannot be undone.
                    </p>
                </Modal>
            </SparkModalPortal>
        </>
    );
};

// ─── Data flattening ────────────────────────────────────────────────────────

function flattenProjectRows(project) {
    const rows = [];
    for (const usecase of project.usecases) {
        rows.push({
            _rowType: 'usecase',
            _rowKey: `usecase-${project.group_name}-${usecase.usecase_name}`,
            _usecase: usecase,
            usecase_name: usecase.usecase_name,
            usecase_goal: usecase.usecase_goal,
            has_multiple_sub_tasks: usecase.has_multiple_sub_tasks,
            llm_usecase_id: usecase.versions[0]?.llm_usecase_id,
        });

        if (usecase.has_multiple_sub_tasks) {
            for (const subTask of usecase.sub_tasks) {
                rows.push({
                    _rowType: 'sub_task',
                    _rowKey: `subtask-${project.group_name}-${usecase.usecase_name}-${subTask.sub_task_key}`,
                    _usecase: usecase,
                    sub_task_name: subTask.sub_task_name,
                    usecase_goal: subTask.usecase_goal,
                });
                for (const version of subTask.versions) {
                    rows.push({
                        ...version,
                        _rowType: 'version',
                        _rowKey: version._id || version.distillation_event_id || `${version.llm_usecase_id}-${version.version}`,
                        _usecase: usecase,
                        _indent: 'deep',
                    });
                }
            }
        } else {
            for (const version of usecase.versions) {
                rows.push({
                    ...version,
                    _rowType: 'version',
                    _rowKey: version._id || version.distillation_event_id || `${version.llm_usecase_id}-${version.version}`,
                    _usecase: usecase,
                    _indent: 'normal',
                });
            }
        }
    }
    return rows;
}

// ─── Main component ─────────────────────────────────────────────────────────

const TrainingsGroupedView = ({
    groupedTrainings,
    onDeleteTraining,
    onDownloadTraining,
    onPushModelTraining,
    onDownloadBenchmarkTraining,
    onDeployTraining,
    onUndeployTraining,
    onDeleteUsecase,
    onViewEditUsecase,
    onEditDataUsecase,
    onTrainUsecase,
    loading = false,
}) => {
    const { role } = useAuth();
    const { allUsecases } = useContext(GlobalContext);

    const [expandedGroups, setExpandedGroups] = useState({});
    const [allExpanded, setAllExpanded] = useState(true);
    const [modelDetailsOpen, setModelDetailsOpen] = useState(false);
    const [modelDetailsVersion, setModelDetailsVersion] = useState(null);
    const [queryFileModal, setQueryFileModal] = useState({
        open: false,
        language: 'python',
        clientId: '',
        usecaseId: '',
        taskName: '',
        projectName: '',
        taskType: undefined,
    });

    const openModelDetails = useCallback((version) => {
        setModelDetailsVersion(version);
        setModelDetailsOpen(true);
    }, []);

    const closeModelDetails = useCallback(() => {
        setModelDetailsOpen(false);
        setModelDetailsVersion(null);
    }, []);

    // Keep the selected version in sync with live updates
    useEffect(() => {
        if (!modelDetailsOpen || !modelDetailsVersion) return;
        const targetId = modelDetailsVersion?.distillation_event_id || modelDetailsVersion?._id;
        if (!targetId) return;

        let latest = null;
        let latestIsThisVersionDeployed = false;
        let latestHasAnyDeployedVersion = false;

        for (const project of groupedTrainings || []) {
            for (const usecase of project.usecases || []) {
                const versions = usecase.versions || [];
                latestHasAnyDeployedVersion = versions.some(
                    (v) => v.deployed_version === v.version && v.is_deployed
                );
                for (const v of versions) {
                    const vId = v.distillation_event_id || v._id;
                    if (vId === targetId) {
                        latest = v;
                        latestIsThisVersionDeployed =
                            v.deployed_version === v.version && v.is_deployed;
                        break;
                    }
                }
                if (latest) break;
            }
            if (latest) break;
        }

        if (!latest) return;
        setModelDetailsVersion((prev) => {
            const next = {
                ...latest,
                isThisVersionDeployed: latestIsThisVersionDeployed,
                hasAnyDeployedVersion: latestHasAnyDeployedVersion,
            };
            if (prev && (prev.distillation_event_id || prev._id) === (next.distillation_event_id || next._id) &&
                prev.usecase_status === next.usecase_status &&
                prev.deploying_version === next.deploying_version &&
                prev.undeploying_version === next.undeploying_version &&
                prev.isThisVersionDeployed === next.isThisVersionDeployed &&
                prev.hasAnyDeployedVersion === next.hasAnyDeployedVersion) {
                return prev;
            }
            return next;
        });
    }, [groupedTrainings, modelDetailsOpen, modelDetailsVersion]);

    // Initialize expanded groups when data changes
    useEffect(() => {
        setExpandedGroups(prev => {
            const next = { ...prev };
            (groupedTrainings || []).forEach(project => {
                if (typeof next[project.group_name] === 'undefined') {
                    next[project.group_name] = true;
                }
            });
            Object.keys(next).forEach(name => {
                if (!(groupedTrainings || []).find(p => p.group_name === name)) {
                    delete next[name];
                }
            });
            return next;
        });
    }, [groupedTrainings]);

    const toggleAllGroups = useCallback(() => {
        const newExpanded = !allExpanded;
        setAllExpanded(newExpanded);
        const newState = {};
        (groupedTrainings || []).forEach(project => {
            newState[project.group_name] = newExpanded;
        });
        setExpandedGroups(newState);
    }, [allExpanded, groupedTrainings]);

    const toggleGroup = useCallback((groupName) => {
        setExpandedGroups(prev => {
            const newState = { ...prev, [groupName]: !prev[groupName] };
            const allGroupsExpanded = (groupedTrainings || []).every(p => newState[p.group_name] === true);
            const allGroupsCollapsed = (groupedTrainings || []).every(p => newState[p.group_name] === false);

            if (allGroupsExpanded) {
                setAllExpanded(true);
            } else if (allGroupsCollapsed) {
                setAllExpanded(false);
            }

            return newState;
        });
    }, [groupedTrainings]);

    const handleDownload = onDownloadTraining;
    const handlePushModel = onPushModelTraining;
    const handleBenchmarkDownload = onDownloadBenchmarkTraining;

    const handleOpenQueryFile = useCallback((version, language) => {
        const usecase = allUsecases?.find(u => u._id === version.llm_usecase_id);
        setQueryFileModal({
            open: true,
            language,
            clientId: version.client_id,
            usecaseId: version.llm_usecase_id,
            taskName: version.usecase_name || usecase?.usecase_name || '',
            projectName: version.group_name || usecase?.group_name || '',
            taskType: version.task_type || usecase?.versions?.[0]?.task_type,
        });
    }, [allUsecases]);

    // Build DataTable columns — includes spacer column for the expand toggle
    const columns = useMemo(() => {
        const cols = [
            {
                key: 'spacer',
                width: 48,
                title: (
                    <Button
                        variant="ghost"
                        size="small"
                        className="spark-table-expand-toggle-btn"
                        aria-expanded={allExpanded}
                        aria-label={allExpanded ? 'Collapse all rows' : 'Expand all rows'}
                        onClick={toggleAllGroups}
                        icon={
                            <SparkIcon
                                slug={allExpanded ? 'chevron-down-large' : 'chevron-right-large'}
                                size="small"
                            />
                        }
                    />
                ),
                render: () => null,
            },
            {
                key: 'name',
                dataIndex: 'usecase_name',
                title: 'Name',
                flex: 2,
                minWidth: 250,
                render: (value, record) => {
                    if (record._rowType === 'usecase') {
                        return (
                            <div style={{ paddingLeft: 8 }}>
                                <ClickableNameLocal
                                    text={record.usecase_name || 'Task name'}
                                    maxWidth={320}
                                    onClick={() => onViewEditUsecase(record.llm_usecase_id)}
                                />
                                {!record.has_multiple_sub_tasks && record.usecase_goal && (
                                    <EllipsisText
                                        text={record.usecase_goal}
                                        maxWidth={320}
                                        style={{ color: 'var(--semantic-content-secondary)', fontSize: 13, marginTop: 2 }}
                                    />
                                )}
                            </div>
                        );
                    }
                    if (record._rowType === 'sub_task') {
                        return (
                            <div style={{ paddingLeft: 24 }}>
                                <span className="fs-unmask" style={{ fontSize: 14, color: 'var(--semantic-content-primary)', fontWeight: 500 }}>
                                    {record.sub_task_name || 'Sub task'}
                                </span>
                                {record.usecase_goal && (
                                    <EllipsisText
                                        text={record.usecase_goal}
                                        maxWidth={320}
                                        style={{ color: 'var(--semantic-content-secondary)', fontSize: 12, marginTop: 2 }}
                                    />
                                )}
                            </div>
                        );
                    }
                    // version row
                    const usecase = record._usecase;
                    const showSubObjective =
                        !usecase?.has_multiple_sub_tasks &&
                        shouldShowTrainingSubObjective(record, usecase?.usecase_name);
                    const isThisVersionDeployed = record.deployed_version === record.version && record.is_deployed;
                    const deploymentScopeVersions = usecase?.has_multiple_sub_tasks
                        ? usecase.versions.filter(v => v.llm_usecase_id === record.llm_usecase_id)
                        : usecase?.versions || [];
                    const hasAnyDeployedVersion = deploymentScopeVersions.some(v => v.deployed_version === v.version && v.is_deployed);

                    return (
                        <div style={{ paddingLeft: record._indent === 'deep' ? 56 : 40 }}>
                            {showSubObjective && (
                                <EllipsisText
                                    text={record.objective_name}
                                    maxWidth={280}
                                    className="fs-unmask"
                                    style={{ fontSize: 12, color: 'var(--semantic-content-secondary)', fontWeight: 500 }}
                                />
                            )}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <ClickableVersionLocal
                                    version={record}
                                    tooltipTitle="Open model details"
                                    onClick={() =>
                                        openModelDetails({
                                            ...record,
                                            isThisVersionDeployed,
                                            hasAnyDeployedVersion,
                                        })
                                    }
                                />
                            </div>
                        </div>
                    );
                },
            },
            {
                key: 'status',
                dataIndex: 'status',
                title: 'Status',
                width: 230,
                render: (value, record) => {
                    if (record._rowType !== 'version') return null;
                    return (
                        <StatusDisplay
                            status={record.status}
                            progress={record.progress}
                            trainingId={record.distillation_event_id}
                            errorDetails={record.error_details}
                            isDeployed={record.deployed_version === record.version && record.is_deployed}
                            usecaseStatus={record.usecase_status}
                            version={record.version}
                            deployingVersion={record.deploying_version}
                            undeployingVersion={record.undeploying_version}
                            usecaseId={record.llm_usecase_id}
                        />
                    );
                },
            },
            {
                key: 'created_by_user',
                dataIndex: 'created_by_user',
                title: 'Created by',
                width: 130,
                render: (value, record) => {
                    if (record._rowType !== 'version') return null;
                    return (
                        <EllipsisText
                            text={value?.name || value?.email || value?.sub || '-'}
                            maxWidth={90}
                            style={{ fontSize: 14 }}
                        />
                    );
                },
            },
            {
                key: 'instance_type',
                dataIndex: 'instance_type',
                title: 'Instance type',
                width: 120,
                render: (value, record) => {
                    if (record._rowType !== 'version') return null;
                    return (
                        <EllipsisText
                            text={value || '—'}
                            maxWidth={110}
                            style={{ fontSize: 14, color: 'var(--semantic-content-primary)' }}
                        />
                    );
                },
            },
            {
                key: 'created_datetime',
                dataIndex: 'created_datetime',
                title: 'Created at',
                width: 140,
                render: (value, record) => {
                    if (record._rowType !== 'version') return null;
                    return <DateTimeDisplay value={value} />;
                },
            },
            {
                key: 'duration',
                dataIndex: 'duration',
                title: 'Duration',
                width: 90,
                render: (value, record) => {
                    if (record._rowType !== 'version') return null;
                    return (
                        <DurationDisplay
                            createdDatetime={record.created_datetime}
                            finishedDatetime={record.finished_datetime}
                            status={record.status}
                        />
                    );
                },
            },
            {
                key: 'actions',
                dataIndex: '_actions',
                title: '',
                width: 56,
                render: (value, record) => {
                    if (record._rowType !== 'version') return null;
                    const usecase = record._usecase;
                    const isThisVersionDeployed = record.deployed_version === record.version && record.is_deployed;
                    const deploymentScopeVersions = usecase?.has_multiple_sub_tasks
                        ? usecase.versions.filter(v => v.llm_usecase_id === record.llm_usecase_id)
                        : usecase?.versions || [];
                    const hasAnyDeployedVersion = deploymentScopeVersions.some(v => v.deployed_version === v.version && v.is_deployed);
                    const versionTaskType = record.task_type || usecase?.versions?.[0]?.task_type;

                    return (
                        <VersionActionsMenu
                            version={record}
                            role={role}
                            isThisVersionDeployed={isThisVersionDeployed}
                            hasAnyDeployedVersion={hasAnyDeployedVersion}
                            openModelDetails={openModelDetails}
                            handleDownload={handleDownload}
                            handlePushModel={handlePushModel}
                            onDeployTraining={onDeployTraining}
                            onUndeployTraining={onUndeployTraining}
                            onDeleteTraining={onDeleteTraining}
                            taskType={versionTaskType}
                            onOpenQueryFile={handleOpenQueryFile}
                        />
                    );
                },
            },
        ];

        return cols;
    }, [
        allExpanded,
        toggleAllGroups,
        role,
        openModelDetails,
        handleDownload,
        handlePushModel,
        onDeleteTraining,
        onDeployTraining,
        onUndeployTraining,
        onViewEditUsecase,
        handleOpenQueryFile,
    ]);

    if (loading) {
        return (
            <div className="trainings-grouped-table-wrapper">
                <DataTable
                    columns={columns}
                    dataSource={[]}
                    loading={true}
                    rowKey="_rowKey"
                />
            </div>
        );
    }

    if (!groupedTrainings || groupedTrainings.length === 0) {
        return (
            <div className="trainings-grouped-table-wrapper">
                <DataTable
                    columns={columns}
                    dataSource={[]}
                    loading={false}
                    rowKey="_rowKey"
                    emptyText="No models found."
                />
            </div>
        );
    }

    return (
        <div className="trainings-grouped-table-wrapper">
            {/* Header-only DataTable — renders column headers */}
            <div className="trainings-header-datatable">
                <DataTable columns={columns} dataSource={[]} showHeader emptyText="" />
            </div>

            {/* Project groups */}
            {groupedTrainings.map(project => {
                const isExpanded = !!expandedGroups[project.group_name];
                const projectRows = isExpanded ? flattenProjectRows(project) : [];

                return (
                    <React.Fragment key={project.group_name}>
                        {/* Expandable group header row — uses Spark Table row classes */}
                        <div
                            className={`spark-table-row spark-table-row-expandable spark-table-row-interactive${isExpanded ? ' spark-table-row-expandable-open' : ''}`}
                            role="row"
                            onClick={() => toggleGroup(project.group_name)}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="spark-table-cell spark-table-cell-expand">
                                <Button
                                    variant="ghost"
                                    size="small"
                                    className="spark-table-expand-toggle-btn"
                                    aria-expanded={isExpanded}
                                    aria-label={isExpanded ? 'Collapse group' : 'Expand group'}
                                    onClick={(e) => { e.stopPropagation(); toggleGroup(project.group_name); }}
                                    icon={
                                        <SparkIcon
                                            slug={isExpanded ? 'chevron-down-large' : 'chevron-right-large'}
                                            size="small"
                                        />
                                    }
                                />
                            </div>
                            <div className="spark-table-cell spark-table-cell-expand-parent">
                                <div className="spark-table-cell-two-lines-content">
                                    <div className="spark-table-cell-title-line">
                                        <p className="spark-table-cell-title-text fs-unmask">
                                            {project.group_name}
                                        </p>
                                        <p className="spark-table-cell-title-count fs-unmask">
                                            ({getUsecaseCount(project)})
                                        </p>
                                    </div>
                                    {project.project_goal && (
                                        <p className="spark-table-cell-subtitle-text">
                                            {project.project_goal}
                                        </p>
                                    )}
                                </div>
                            </div>
                            <div className="spark-table-cell spark-table-cell-text-button" />
                        </div>

                        {/* DataTable for this project's rows */}
                        {isExpanded && projectRows.length > 0 && (
                            <DataTable
                                columns={columns}
                                dataSource={projectRows}
                                rowKey="_rowKey"
                                loading={false}
                                showHeader={false}
                                disableHoverBackground
                            />
                        )}
                    </React.Fragment>
                );
            })}

            <ModelDetailsDrawer
                open={modelDetailsOpen}
                onClose={closeModelDetails}
                distillationEventId={modelDetailsVersion?.distillation_event_id}
                version={modelDetailsVersion}
                onDownloadModel={handleDownload}
                onDownloadBenchmark={handleBenchmarkDownload}
                onDeploy={onDeployTraining}
                onUndeploy={onUndeployTraining}
                role={role}
                title={
                    modelDetailsVersion
                        ? formatTrainingModelDetailsTitle({
                            umbrellaName: modelDetailsVersion.usecase_name,
                            objectiveName: modelDetailsVersion.objective_name,
                            version: modelDetailsVersion.version ?? 1,
                        })
                        : 'Model details'
                }
                clientId={modelDetailsVersion?.client_id}
                taskType={modelDetailsVersion?.task_type}
            />

            <QueryFileModal
                open={queryFileModal.open}
                language={queryFileModal.language}
                onClose={() => setQueryFileModal((prev) => ({ ...prev, open: false }))}
                clientId={queryFileModal.clientId}
                usecaseId={queryFileModal.usecaseId}
                taskName={queryFileModal.taskName}
                projectName={queryFileModal.projectName}
                taskType={queryFileModal.taskType}
            />
        </div>
    );
};

export default TrainingsGroupedView;
